'use client';

import {
  NavigationChevronLeft,
  NavigationChevronRight,
} from '@energy-retailer/icons';
import { Transition } from '@headlessui/react';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { Button } from '@/components/button';
import { ContentfulIcon } from '@/components/icon';
import { Text } from '@/components/text';
import { getPageUrl } from '@/lib/utils/get-page-url';

import { useEnvContext } from '../../../providers';
import { NavigationButton } from '../navigation-button';

import type { HeaderDropdown, Page } from '@/app/generated/graphql';
import { ANALYTICS_EVENTS } from '@/constants';

type MobileNavigationProps = {
  items: HeaderDropdown[];
  isSignUp?: boolean;
};

const QuickLinksMenu = ({
  quickLinksItems,
  onBackClicked,
  menuCategory,
}: {
  quickLinksItems?: Page[];
  onBackClicked: () => void;
  menuCategory: string;
}) => {
  const pathname = usePathname();

  return (
    <>
      <NavigationButton
        onClick={onBackClicked}
        className="border-b md:hidden"
      >
        <NavigationChevronLeft className="h-icon w-icon text-pink" />
        Back
      </NavigationButton>
      <Text
        className="p-d pb-a"
        weight="semi"
        variant="body2"
      >
        Quick Links
      </Text>
      {quickLinksItems?.map((quickLink) => {
        return (
          <NavigationButton
            key={quickLink.slug}
            href={getPageUrl(quickLink)}
            onClick={() => {
              trackAnalyticsEvent(ANALYTICS_EVENTS.headerDropdown.selected, {
                currentLocation: pathname,
                destination: getPageUrl(quickLink),
                menuCategory,
              });
            }}
          >
            {quickLink.icon && quickLink.icon.id ? (
              <ContentfulIcon
                className="mr-a h-icon w-icon text-pink-500"
                id={quickLink.icon.id}
              />
            ) : null}
            {quickLink.title}
          </NavigationButton>
        );
      })}
    </>
  );
};

export const MobileNavigation = ({
  items,
  isSignUp,
}: MobileNavigationProps) => {
  const [selectedNavId, setSelectedNavId] = useState<string | null>(null);
  const selectedNav = items.find((item) => item.sys.id === selectedNavId);

  const { LOGIN_URL } = useEnvContext();

  // Preselect first item on tablet (not on mobile)
  useEffect(() => {
    const checkWidth = () => {
      const mediaQuery = window.matchMedia(`(min-width: 768px)`);
      if (mediaQuery.matches) {
        setSelectedNavId(items[0].sys.id);
      }
    };

    // Run on initial load
    checkWidth();

    // Run on resize
    window.addEventListener('resize', checkWidth);

    return () => {
      window.removeEventListener('resize', checkWidth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on first mount
  }, []);

  const goBack = () => {
    setSelectedNavId(null);
  };

  return (
    <>
      <nav className="flex h-full w-full flex-1 flex-col">
        <div className="grow">
          {items.map((item) => {
            const isSelected = item.sys.id === selectedNavId;

            return (
              <NavigationButton
                className={`flex w-full justify-between px-e py-d sm:px-d sm:py-c ${isSelected && 'bg-gray-50 text-pink-500'
                  }`}
                key={item.sys.id}
                onClick={() => {
                  setSelectedNavId(item.sys.id);
                }}
              >
                {item.title}
                <NavigationChevronRight className="h-icon w-icon text-pink" />
              </NavigationButton>
            );
          })}
        </div>
        {!isSignUp && (
          <Button
            className="mx-d mb-e"
            href={LOGIN_URL}
          >
            Login
          </Button>
        )}
      </nav>
      {/* Tablet */}
      <nav className="z-30 hidden h-[calc(100vh-56px)] w-full flex-1 flex-col border-l bg-white md:flex">
        <QuickLinksMenu
          menuCategory={selectedNav?.title ?? ''}
          onBackClicked={goBack}
          quickLinksItems={selectedNav?.quickLinksCollection?.items as Page[]}
        />
      </nav>
      {/* Mobile */}
      <Transition
        show={Boolean(selectedNavId)}
        enter="transition-transform ease-out duration-300"
        enterFrom="transform translate-x-full"
        enterTo="transform translate-x-0"
        leave="transition-transform ease-in duration-300"
        leaveFrom="transform translate-x-0"
        leaveTo="transform translate-x-full"
        as="div"
        className="fixed z-30 flex h-[calc(100vh-56px)] w-full flex-1 flex-col bg-white md:hidden"
      >
        <QuickLinksMenu
          menuCategory={selectedNav?.title ?? ''}
          quickLinksItems={selectedNav?.quickLinksCollection?.items as Page[]}
          onBackClicked={goBack}
        />
      </Transition>
    </>
  );
};
